import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Calendar } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";

export const Route = createFileRoute("/tools/maintenance")({
  head: () => ({ meta: [{ title: "Maintenance Agreements" }, { name: "robots", content: "noindex" }] }),
  component: MaintPage,
});

type M = {
  id?: string; client_id?: string | null; client_name: string; client_email: string | null; client_phone: string | null; client_address: string | null;
  plan_name: string; price: number; billing_cycle: string; visit_frequency_months: number;
  last_visit_on: string | null; next_visit_on: string | null; status: string; notes: string | null;
};

const empty = (): M => ({ client_name: "", client_email: "", client_phone: "", client_address: "", plan_name: "Standard Care", price: 500, billing_cycle: "annual", visit_frequency_months: 12, last_visit_on: null, next_visit_on: null, status: "active", notes: "" });

function addMonths(d: string, m: number) { const dt = new Date(d + "T00:00:00"); dt.setMonth(dt.getMonth() + m); return dt.toISOString().slice(0, 10); }
function daysUntil(d: string | null) { if (!d) return null; const ms = new Date(d + "T00:00:00").getTime() - Date.now(); return Math.ceil(ms / 86400000); }

function MaintPage() {
  const [list, setList] = useState<M[]>([]);
  const [current, setCurrent] = useState<M | null>(null);

  const load = async () => {
    const { data } = await supabase.from("maintenance_agreements").select("*").order("next_visit_on", { ascending: true, nullsFirst: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current || !current.client_name || !current.plan_name) return toast.error("Client and plan required");
    const payload = { ...current };
    if (payload.last_visit_on && payload.visit_frequency_months) payload.next_visit_on = addMonths(payload.last_visit_on, payload.visit_frequency_months);
    if (current.id) {
      const { error } = await supabase.from("maintenance_agreements").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("maintenance_agreements").insert(payload as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("maintenance_agreements").delete().eq("id", id); load(); } };
  const markVisited = async (m: M) => {
    const today = new Date().toISOString().slice(0, 10);
    await supabase.from("maintenance_agreements").update({ last_visit_on: today, next_visit_on: addMonths(today, m.visit_frequency_months) }).eq("id", m.id!);
    toast.success("Visit logged"); load();
  };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Maintenance Agreements</h1>
          <p className="text-sm text-muted-foreground mt-1">Recurring service plans and upcoming visits.</p>
        </div>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New Plan</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(m => {
          const days = daysUntil(m.next_visit_on);
          const overdue = days !== null && days < 0;
          const soon = days !== null && days >= 0 && days <= 30;
          return (
            <div key={m.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{m.client_name}</h3>
                  <p className="text-sm text-muted-foreground">{m.plan_name} · ${m.price}/{m.billing_cycle}</p>
                  <p className="text-xs mt-1">
                    <span className={`rounded-full px-2 py-0.5 ${m.status === 'active' ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted'}`}>{m.status}</span>
                    {m.next_visit_on && <span className={`ml-2 ${overdue ? 'text-destructive font-semibold' : soon ? 'text-amber-600' : 'text-muted-foreground'}`}>Next: {m.next_visit_on} ({overdue ? `${-days!}d overdue` : days !== null ? `in ${days}d` : ''})</span>}
                  </p>
                </div>
                <button onClick={() => del(m.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setCurrent(m)}>Open</Button>
                <Button size="sm" variant="secondary" onClick={() => markVisited(m)}><Calendar className="h-4 w-4 mr-1" />Log Visit</Button>
              </div>
            </div>
          );
        })}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No plans yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name ?? "", client_email: f.client_email, client_phone: f.client_phone, client_address: f.client_address })} /></div>
        <div><label className="text-xs text-muted-foreground">Client Name</label><Input value={current.client_name} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Client Email</label><Input value={current.client_email || ""} onChange={e => setCurrent({ ...current, client_email: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Phone</label><Input value={current.client_phone || ""} onChange={e => setCurrent({ ...current, client_phone: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Address</label><Input value={current.client_address || ""} onChange={e => setCurrent({ ...current, client_address: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Plan Name</label><Input value={current.plan_name} onChange={e => setCurrent({ ...current, plan_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Status</label>
          <select className="w-full rounded border border-input bg-background p-2" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
            <option value="active">Active</option><option value="paused">Paused</option><option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">Price ($)</label><NumberInput value={current.price} onValueChange={n => setCurrent({ ...current, price: n })} /></div>
        <div><label className="text-xs text-muted-foreground">Billing Cycle</label>
          <select className="w-full rounded border border-input bg-background p-2" value={current.billing_cycle} onChange={e => setCurrent({ ...current, billing_cycle: e.target.value })}>
            <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option><option value="one_time">One-time</option>
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">Visit Frequency (months)</label><NumberInput min={1} value={current.visit_frequency_months} onValueChange={n => setCurrent({ ...current, visit_frequency_months: n })} /></div>
        <div><label className="text-xs text-muted-foreground">Last Visit</label><Input type="date" value={current.last_visit_on || ""} onChange={e => setCurrent({ ...current, last_visit_on: e.target.value || null })} /></div>
        <div><label className="text-xs text-muted-foreground">Next Visit (auto)</label><Input type="date" value={current.next_visit_on || ""} onChange={e => setCurrent({ ...current, next_visit_on: e.target.value || null })} /></div>
      </div>
      <Textarea placeholder="Notes" className="mt-4" value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
    </div>
  );
}
